import { useQuery } from "@apollo/client";
import {
  Container,
  Icon,
  Table,
  Text,
  useFormContext,
} from "@fluidattacks/design";
import type { ColumnDef } from "@tanstack/react-table";
import { isEmpty } from "lodash";
import { useCallback, useState } from "react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";

import { GET_FINDING_VULNS_TO_REATTACK } from "./queries";
import type {
  IVulnerabilitiesToReattackTableProps,
  IVulnerabilityAttr,
} from "./types";

import type { IFormValues } from "../../add-modal";
import { useTable } from "hooks/use-table";
import { Logger } from "utils/logger";

const MIN_VULNS_TO_REATTACK = 10;

const VulnerabilitiesToReattackTable: React.FC<
  IVulnerabilitiesToReattackTableProps
> = ({ finding }): JSX.Element => {
  const { t } = useTranslation();
  const { setValue, trigger, watch } = useFormContext<IFormValues>();
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const [lastSelectedIds, setLastSelectedIds] = useState<string[]>([]);
  const onItemClick = useCallback((): void => {
    setIsExpanded((prevState): boolean => !prevState);
  }, []);

  const { data } = useQuery(GET_FINDING_VULNS_TO_REATTACK, {
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        Logger.error("Couldn't load vulns to reattack", error);
      });
    },
    variables: {
      findingId: finding.id,
    },
  });
  const vulnsToReattackConnection =
    data === undefined
      ? undefined
      : data.finding.vulnerabilitiesToReattackConnection;
  const vulnsToReattackEdges =
    vulnsToReattackConnection === undefined
      ? []
      : vulnsToReattackConnection.edges;
  const vulnsToReattack = vulnsToReattackEdges
    .map(
      (vulnEdge): IVulnerabilityAttr =>
        vulnEdge?.node ?? {
          __typename: undefined,
          findingId: "",
          id: "",
          specific: "",
          where: "",
        },
    )
    .filter(
      (vuln): boolean =>
        vuln.where.toLowerCase().includes(search.toLowerCase()) ||
        vuln.specific.toLowerCase().includes(search.toLowerCase()),
    );

  const columns: ColumnDef<IVulnerabilityAttr>[] = [
    { accessorKey: "where", header: "Where" },
    { accessorKey: "specific", header: "Specific" },
  ];

  const { table } = useTable({
    columns,
    data: vulnsToReattack,
    id: `vulnerabilities-to-reattack-${finding.id}`,
    options: {
      actionsOptions: {
        onSearch: setSearch,
        search,
        searchPlaceholder: t("table.search"),
      },
      filtersMode: "client-side",
      paginationMode: "client-side",
      rowOptions: {
        enableMultiSelection: true,
      },
    },
    total: vulnsToReattack.length,
  });

  const handleRowSelectionChange = useCallback(
    (selectedIds: string[]): void => {
      if (!isEmpty(vulnsToReattack)) {
        const selectedVulnIds = selectedIds.map((rowId): string => {
          const row = table.getRow(rowId);
          const vuln = row.original;

          return `${vuln.findingId} ${vuln.id}`;
        });

        const affectedReattacks = watch("affectedReattacks");
        const [{ findingId }] = vulnsToReattack;
        const currentIds = affectedReattacks
          .filter(
            (id): boolean =>
              selectedVulnIds.includes(id) || !id.startsWith(findingId),
          )
          .concat(
            selectedVulnIds.filter(
              (id): boolean => !affectedReattacks.includes(id),
            ),
          );

        setValue("affectedReattacks", currentIds);
        void trigger("affectedReattacks");
      }
    },
    [vulnsToReattack, watch, setValue, trigger, table],
  );

  // Subscribe to row selection changes
  const { rowSelection } = table.getState();
  const selectedIds = Object.keys(rowSelection);
  if (
    selectedIds.length > 0 &&
    JSON.stringify(selectedIds) !== JSON.stringify(lastSelectedIds)
  ) {
    setLastSelectedIds(selectedIds);
    handleRowSelectionChange(selectedIds);
  }

  const theme = useTheme();

  if (finding.verified) {
    return <div key={finding.id} />;
  }

  return (
    <Container borderColor={theme.palette.gray[200]} borderTop={"1px solid"}>
      <Container
        bgColor={theme.palette.white}
        borderBottom={"1px solid"}
        borderColor={theme.palette.gray[200]}
        key={finding.title}
      >
        <Container
          aria-controls={`panel-${finding.title}`}
          aria-expanded={isExpanded}
          aria-label={finding.title}
          bgColor={theme.palette.white}
          bgColorHover={theme.palette.gray[100]}
          cursor={"pointer"}
          display={isExpanded ? "visible" : "block"}
          onClick={onItemClick}
          px={0.75}
          py={0.75}
          width={"100%"}
        >
          <Container
            as={"summary"}
            display={"flex"}
            flexDirection={"row"}
            gap={0.75}
            justify={"space-between"}
            width={"100%"}
          >
            <Text
              color={theme.palette.gray[800]}
              fontWeight={"bold"}
              size={"md"}
              textAlign={"start"}
            >
              {finding.title}
            </Text>
            <Icon
              icon={isExpanded ? "chevron-up" : "chevron-down"}
              iconColor={theme.palette.gray[400]}
              iconSize={"lg"}
              iconType={"fa-light"}
            />
          </Container>
        </Container>
        <Container
          display={isExpanded ? "block" : "none"}
          id={`panel-${finding.title}`}
          px={0.75}
          py={0.75}
        >
          <Table table={table}>
            <Table.Options />
            <Table.Content />
            {vulnsToReattack.length > MIN_VULNS_TO_REATTACK ? (
              <Table.Footer />
            ) : undefined}
          </Table>
        </Container>
      </Container>
    </Container>
  );
};
export { VulnerabilitiesToReattackTable };
